/**
 * Tiny request router for the mock API. Each domain file exports a `Route[]`;
 * `handlers/index.ts` concatenates them. Paths are matched AFTER the board
 * prefix is stripped (see interceptor), so register `/api/incidents/stats`
 * once and check `ctx.board` if the board twin returns fewer fields.
 */
import { Persona } from './personas';

export interface MockContext {
    method: string;
    /** Normalised path, e.g. /api/incidents/stats (no query, no /board). */
    path: string;
    query: URLSearchParams;
    /** Parsed JSON body for POSTs (ngxh6 sends JSON strings); FormData passes through. */
    body: any;
    /** `:name` path segments. */
    params: Record<string, string>;
    persona: Persona;
    /** X-Switch-User header (admin impersonation), or '' when not impersonating. */
    switchUser: string;
    /** True when the frontend called the /api/board/... twin. */
    board: boolean;
}

export interface MockResponse {
    status?: number;
    body: unknown;
    headers?: Record<string, string>;
}

/** Return a plain body (sent as 200) or a MockResponse for status/headers. */
export type Handler = (ctx: MockContext) => unknown | MockResponse;

export interface Route {
    method: 'GET' | 'POST' | 'ANY';
    /** `/api/quarterly-report/:quarter` — `:name` matches one segment, a trailing `/*` matches the rest. */
    path: string;
    handler: Handler;
}

export const respond = (body: unknown, status = 200, headers?: Record<string, string>): MockResponse => ({ status, body, headers, __mock: true }) as MockResponse;

export function isMockResponse(v: unknown): v is MockResponse {
    return !!v && typeof v === 'object' && (v as any).__mock === true;
}

/** API Platform-style paginated collection: plain array + X-Page headers. */
export function paginated<T>(items: T[], query: URLSearchParams, defaultSize = 10): MockResponse {
    const page = Math.max(1, Number(query.get('page') || 1));
    const size = Math.max(1, Number(query.get('itemsPerPage') || query.get('perPage') || defaultSize));
    const slice = items.slice((page - 1) * size, page * size);
    return respond(slice, 200, { 'X-Page': String(page), 'X-Page-Size': String(size), 'X-Collection-Size': String(items.length) });
}

interface Compiled extends Route {
    re: RegExp;
    keys: string[];
}

export class MockRouter {
    private compiled: Compiled[];

    constructor(routes: Route[]) {
        this.compiled = routes.map(r => {
            const keys: string[] = [];
            const pattern = r.path
                .replace(/\/$/, '')
                .replace(/[.+?^${}()|[\]\\]/g, '\\$&')
                .replace(/\/\*$/, '(?:/(?<rest>.*))?')
                .replace(/:([A-Za-z_]\w*)/g, (_, k) => {
                    keys.push(k);
                    return `(?<${k}>[^/]+)`;
                });
            return { ...r, keys, re: new RegExp(`^${pattern}/?$`) };
        });
    }

    match(method: string, path: string): { route: Route; params: Record<string, string> } | null {
        for (const r of this.compiled) {
            if (r.method !== 'ANY' && r.method !== method) {
                continue;
            }
            const m = r.re.exec(path);
            if (m) {
                const params: Record<string, string> = {};
                for (const [k, v] of Object.entries(m.groups || {})) {
                    if (v !== undefined) {
                        params[k] = decodeURIComponent(v);
                    }
                }
                return { route: r, params };
            }
        }
        return null;
    }
}
